import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Search, X, Film, Eye, Trash2, Globe, Lock } from "lucide-react";
import { Btn, Empty, Input, Select, Spinner, RecreacionBadge } from "./ui";
import { MediaPreview } from "./MediaPreview";
import { errMsg, fmtDate, fmtSize, triggerDownload } from "./api";

const Thumb = ({ client, c }) =>
  c.thumb_file_id ? (
    <MediaPreview client={client} fid={c.thumb_file_id} tipo="imagen" className="w-full h-full" />
  ) : (
    <div className="w-full h-full bg-[#0A192F] flex items-center justify-center text-[#F5B400]"><Film size={34} /></div>
  );

// Shared library for admins (isAdmin) and collaborators.
export const Biblioteca = ({ client, meta, isAdmin = false, refreshKey = 0 }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [prov, setProv] = useState("");
  const [tipo, setTipo] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/contenidos", { params: { q, categoria: cat, provincia: prov, tipo } });
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
      setItems([]);
    }
  }, [client, q, cat, prov, tipo]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load, refreshKey]);

  const download = async (c) => {
    try {
      const { data } = await client.post(`/contenidos/${c.id}/descargar`);
      triggerDownload(data.url);
      setItems((xs) => xs.map((x) => (x.id === c.id ? { ...x, descargas: (x.descargas || 0) + 1 } : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const patch = async (c, body, msg) => {
    try {
      await client.patch(`/contenidos/${c.id}`, body);
      toast.success(msg);
      setOpen(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Quitar "${c.titulo}" de la biblioteca?`)) return;
    try {
      await client.delete(`/contenidos/${c.id}`);
      toast.success("Contenido quitado");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="red-biblioteca">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar título, lugar, autor…" className="pl-9" data-testid="biblio-buscar" />
        </div>
        <Select value={cat} placeholder="Todas las categorías" options={meta.categorias} onChange={(e) => setCat(e.target.value)} data-testid="biblio-categoria" />
        <Select value={prov} placeholder="Todas las provincias" options={meta.provincias} onChange={(e) => setProv(e.target.value)} data-testid="biblio-provincia" />
        <Select value={tipo} placeholder="Videos e imágenes" options={[{ value: "video", label: "Solo videos" }, { value: "imagen", label: "Solo imágenes" }]} onChange={(e) => setTipo(e.target.value)} data-testid="biblio-tipo" />
      </div>

      {items === null ? (
        <Spinner />
      ) : items.length === 0 ? (
        <Empty>No hay contenidos con esos filtros.</Empty>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 group flex flex-col" data-testid={`biblio-item-${c.id}`}>
              <button type="button" onClick={() => setOpen(c)} className="relative aspect-video overflow-hidden" data-testid={`biblio-preview-${c.id}`}>
                <Thumb client={client} c={c} />
                <span className="absolute top-2 left-2 bg-[#0A192F]/85 text-white text-[10px] font-headings uppercase tracking-wider px-2 py-0.5">{c.tipo}</span>
                <span className="absolute inset-0 bg-[#0A192F]/0 group-hover:bg-[#0A192F]/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Eye className="text-white" />
                </span>
              </button>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <span className="text-[10px] font-headings uppercase tracking-wider text-[#0A2463]">{c.categoria}</span>
                  {c.es_recreacion && <RecreacionBadge />}
                </div>
                <h4 className="font-semibold text-[#0A192F] leading-snug line-clamp-2">{c.titulo}</h4>
                <p className="text-xs text-[#6B7280] mt-1">{c.localidad}, {c.provincia} · {fmtDate(c.fecha)}</p>
                <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                  <span className="text-xs text-[#6B7280]">{c.descargas || 0} descargas</span>
                  <Btn variant="ghost" className="!px-3 !py-1.5 !text-xs" onClick={() => download(c)} data-testid={`biblio-descargar-${c.id}`}>
                    <Download size={14} /> Descargar
                  </Btn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={() => setOpen(null)} data-testid="biblio-modal">
          <div className="bg-white w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start gap-3 p-4 border-b">
              <div>
                <div className="flex flex-wrap gap-2 items-center mb-1">
                  <span className="text-[10px] font-headings uppercase tracking-wider text-[#0A2463]">{open.categoria}</span>
                  {open.es_recreacion && <RecreacionBadge />}
                </div>
                <h3 className="font-headings uppercase text-xl text-[#0A192F]">{open.titulo}</h3>
              </div>
              <button onClick={() => setOpen(null)} data-testid="biblio-modal-close"><X /></button>
            </div>
            <MediaPreview client={client} fid={open.file_id} tipo={open.tipo} className="w-full max-h-[60vh] object-contain bg-black" />
            <div className="p-5 space-y-3">
              <p className="text-[#374151] whitespace-pre-line">{open.descripcion}</p>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Lugar</dt><dd>{open.localidad}, {open.provincia}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Fecha</dt><dd>{fmtDate(open.fecha)}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Autor</dt><dd>{open.autor}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wider text-gray-500">Archivo</dt><dd>{fmtSize(open.size)}</dd></div>
              </dl>
              <div className="flex flex-wrap gap-2 pt-2">
                <Btn variant="gold" onClick={() => download(open)} data-testid="biblio-modal-descargar"><Download size={15} /> Descargar</Btn>
                {isAdmin && (
                  <>
                    {open.visibilidad === "publico" ? (
                      <Btn variant="ghost" onClick={() => patch(open, { visibilidad: "colaboradores" }, "Ahora solo lo ven colaboradores")} data-testid="biblio-hacer-privado"><Lock size={15} /> Quitar de la biblioteca pública</Btn>
                    ) : (
                      <Btn variant="ghost" onClick={() => patch(open, { visibilidad: "publico" }, "Marcado para la biblioteca pública")} data-testid="biblio-hacer-publico"><Globe size={15} /> Mostrar en biblioteca pública</Btn>
                    )}
                    <Btn variant="ghost" onClick={() => patch(open, { es_recreacion: !open.es_recreacion }, "Etiqueta actualizada")} data-testid="biblio-toggle-recreacion">
                      {open.es_recreacion ? "Quitar etiqueta recreación" : "Marcar como recreación"}
                    </Btn>
                    <Btn variant="danger" onClick={() => remove(open)} data-testid="biblio-eliminar"><Trash2 size={15} /> Quitar</Btn>
                  </>
                )}
              </div>
              {isAdmin && (
                <p className="text-xs text-gray-500">
                  Visible para: <strong>{open.visibilidad === "publico" ? "colaboradores y biblioteca pública" : "solo colaboradores"}</strong> · Origen: {open.origen === "envio" ? "envío de colaborador" : "carga de administración"}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Biblioteca;
